import {
  PreScreeningQuestion,
  QuestionType,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  CLINICAL_TRIAL_ATTRIBUTES,
  IMMUNOSUPPRESSION_ATTRIBUTES,
  INFECTIOUS_DISEASE_ATTRIBUTES,
  CANCER_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Norovirus Vaccine Study
 * Target population: Adults 60+ interested in norovirus (stomach flu) vaccine clinical trial
 * Trial ID: VMODNOROP301B
 */
export const norovirusVaccineScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "59_or_under",
        label: "59 or under",
        disqualify: true
      },
      {
        value: "60_to_69",
        label: "60 to 69"
      },
      {
        value: "70_to_79",
        label: "70 to 79"
      },
      {
        value: "80_or_older",
        label: "80 or older"
      },
    ],
    disqualifyMessage: "This study is for adults aged 60 and older.",
    attributeMapping: mappers.value({
      "59_or_under": [],
      "60_to_69": [],
      "70_to_79": [],
      "80_or_older": [],
    }),
  },

  {
    id: "medical_conditions_exclusion",
    type: QuestionType.CHECKBOX,
    question: "Have you experienced or been diagnosed with any of the following conditions?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "pericarditis_myocarditis",
        label: "Myocarditis (heart muscle inflammation) or pericarditis (heart lining inflammation) in the past 6 months",
        disqualify: true
      },
      {
        value: "guillain_barre",
        label: "Guillain-Barré syndrome",
        disqualify: true
      },
      {
        value: "bleeding_disorder",
        label: "Bleeding disorder (such as hemophilia or von Willebrand disease)",
        disqualify: true
      },
      {
        value: "cancer_recent",
        label: "Cancer within the past 2 years (excluding skin cancers such as basal cell carcinoma/BCC and squamous cell carcinoma/SCC)",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        pericarditis_myocarditis: [
          { attributeDefinitionId: 25888, booleanAttributeValue: true }, // history_myocarditis_pericarditis_prerequisite
        ],
        guillain_barre: [
          { attributeDefinitionId: 25742, booleanAttributeValue: true }, // had_guillianbarre_prerequisite
        ],
        bleeding_disorder: [
          { attributeDefinitionId: 15061, booleanAttributeValue: true }, // has_bleedingdisorder
        ],
        cancer_recent: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
        ],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: 25888, booleanAttributeValue: false }, // history_myocarditis_pericarditis_prerequisite
          { attributeDefinitionId: 25742, booleanAttributeValue: false }, // had_guillianbarre_prerequisite
          { attributeDefinitionId: 15061, booleanAttributeValue: false }, // has_bleedingdisorder
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "immunocompromised_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do any of these apply to you?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "organ_transplant",
        label: "I have had an organ transplant or I am currently on the transplant waitlist",
        disqualify: true
      },
      {
        value: "immunosuppressive_medications",
        label: "I have taken long term immunosuppressive medications in the past 6 months (such as prednisone, methotrexate, azathioprine, cyclosporine, mycophenolate, rituximab, or biologics)",
        disqualify: true
      },
      {
        value: "hiv",
        label: "I have HIV that is untreated or uncontrolled (with a high viral load or a low CD4 count)",
        disqualify: true
      },
      {
        value: "immune_condition",
        label: "I have a condition that affects my immune system, such as an immune deficiency, a weakened immune system from medications, removal of my spleen, or repeated serious infections",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants who are immunocompromised or taking immunosuppressive medications.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        organ_transplant: [
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: true },
        ],
        immunosuppressive_medications: [
          { attributeDefinitionId: 25884, booleanAttributeValue: true }, // is_immunocompromised_prerequisite
        ],
        hiv: [
          { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: true },
        ],
        immune_condition: [
          { attributeDefinitionId: 25884, booleanAttributeValue: true }, // is_immunocompromised_prerequisite
        ],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: false },
          { attributeDefinitionId: 25884, booleanAttributeValue: false },
          { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "recent_medications",
    type: QuestionType.CHECKBOX,
    question: "Have you recently received any of the following medications or treatments?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "ivig_3_months",
        label: "I have received IVIg (intravenous immunoglobulin) in the past 3 months",
        disqualify: true
      },
      {
        value: "blood_products_3_months",
        label: "I have received blood products (such as a blood transfusion, plasma, or platelets) in the past 3 months",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants who have recently received these medications or treatments.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        ivig_3_months: [
          { attributeDefinitionId: 21642, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        blood_products_3_months: [
          { attributeDefinitionId: 23724, booleanAttributeValue: true }, // had_blood_products_3_months
        ],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: 21642, medicationAttributeValue: { hasTaken: false, intervals: [] } },
          { attributeDefinitionId: 23724, booleanAttributeValue: false }, // had_blood_products_3_months
        ],
      },
    }),
  },

  {
    id: "concurrent_trial",
    type: QuestionType.RADIO,
    question: "Have you participated in a clinical trial in the past 30 days?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I have not participated in a clinical trial in the past 30 days"
      },
      {
        value: "yes",
        label: "Yes, I have participated in a clinical trial in the past 30 days",
        disqualify: true
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have been in a clinical trial within the past 30 days cannot participate in this study.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },
];

